import type { ReactNode } from "react";

// ===========================================================================
// AspectFit — encaja un hijo de proporción fija (16:9 por omisión) en el
// espacio disponible del contenedor, sin desbordar ni a lo ancho ni a lo alto.
// El contenedor es un «size container» (CSS container queries): el hijo mide
// `min(100cqw, 100cqh × ratio)` de ancho y deriva su alto con aspect-ratio, así
// el mosaico de cámaras ocupa todo el alto libre de la ventana sin depender de
// cuánto mide el árbol de la izquierda ni de un `100vh - Xrem` adivinado.
// El padre debe darle altura (flex-1 + min-h-0, o una altura explícita).
// Respaldo sin container queries: el navegador descarta el `width` en línea y la
// caja queda con `w-full` + aspect-ratio, limitada por `max-h-full`.
// ===========================================================================

export interface AspectFitProps {
  /** Ancho ÷ alto. 16/9 por omisión. */
  ratio?: number;
  className?: string;
  children: ReactNode;
}

export default function AspectFit({ ratio = 16 / 9, className = "", children }: AspectFitProps) {
  return (
    <div
      className={`flex min-h-0 min-w-0 flex-1 items-center justify-center ${className}`}
      style={{ containerType: "size" }}
      data-testid="aspect-fit"
    >
      <div
        className="max-h-full w-full max-w-full"
        data-testid="aspect-fit-box"
        data-ratio={ratio}
        style={{ width: `min(100cqw, calc(100cqh * ${ratio}))`, aspectRatio: String(ratio) }}
      >
        {children}
      </div>
    </div>
  );
}
